import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Grid, Button, TextField } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedIcon from '@mui/icons-material/Verified';
import BuildIcon from '@mui/icons-material/Build';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Layout from '../components/Layout';
import { sendEmail } from '../utils/emailService';

const features = [
  { icon: <InventoryIcon sx={{ fontSize: 26 }} />, title: 'Digital Product Records', description: 'Replace spreadsheets with a structured, searchable database of every product you manufacture or service.' },
  { icon: <SearchIcon sx={{ fontSize: 26 }} />, title: 'Instant Search', description: 'Find any product, warranty, or service record in seconds — no more scrolling through Excel rows.' },
  { icon: <VerifiedIcon sx={{ fontSize: 26 }} />, title: 'Warranty Management', description: 'Track warranty periods, flag expiring items, and maintain a clean audit trail automatically.' },
  { icon: <BuildIcon sx={{ fontSize: 26 }} />, title: 'Quality & Service Logs', description: 'Document quality checks and service history against each product for compliance and accountability.' },
];

const useCases = [
  { title: 'Manufacturing SMEs', body: 'Stop managing production records on Excel. Get a live, searchable database your whole team can use.' },
  { title: 'Service Centers', body: 'Maintain complete service history per unit — warranties, repairs, and quality checks in one place.' },
  { title: 'Quality Teams', body: 'Log inspections, flag issues, and generate reports without chasing paper forms.' },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#F9FAFB', borderRadius: '10px',
    '& fieldset': { borderColor: '#E5E7EB' },
    '&:hover fieldset': { borderColor: 'rgba(99,102,241,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#6366F1', borderWidth: '1px' },
  },
  '& .MuiInputLabel-root': { color: '#9CA3AF' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#6366F1' },
  '& .MuiOutlinedInput-input': { color: '#0F1117' },
};

const LineDeskPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const result = await sendEmail(formData, 'LineDesk Demo Request');
    setSending(false);
    if (result.success) {
      alert("Thanks! We'll reach out to schedule your demo.");
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    } else {
      alert('Failed to send. Please email admin@infynihub.com directly.');
    }
  };

  const set = (f) => (e) => setFormData({ ...formData, [f]: e.target.value });

  return (
    <Layout>
      <Box sx={{ bgcolor: '#FFFFFF', pt: { xs: '140px', md: '150px' } }}>

        {/* Hero */}
        <Box sx={{ py: { xs: 6, md: 10 }, position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', top: 0, right: 0, width: { xs: 250, md: 500 }, height: { xs: 250, md: 500 }, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: '100px', px: 2, py: 0.6, mb: 3 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366F1' }} />
              <Typography sx={{ color: '#4F46E5', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Manufacturing & Production</Typography>
            </Box>
            <Typography variant="h1" sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '2.4rem', sm: '3rem', md: '3.8rem' }, lineHeight: 1.1, letterSpacing: '-0.03em', mb: 2 }}>
              LineDesk
            </Typography>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: { xs: '1rem', md: '1.15rem' }, mb: 3 }}>Production Streamline System</Typography>
            <Typography sx={{ color: '#6B7280', fontSize: { xs: '0.95rem', md: '1.05rem' }, lineHeight: 1.85, maxWidth: 580, mb: 5 }}>
              Built for manufacturers still tracking product quality, warranties, and service history on Excel or paper. LineDesk gives you a clean, searchable digital system your whole team can actually use.
            </Typography>
            <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />}
              onClick={() => document.getElementById('linedesk-contact').scrollIntoView({ behavior: 'smooth' })}
              sx={{ bgcolor: '#6366F1', color: 'white', px: { xs: 3, md: 4 }, py: 1.6, fontWeight: 700, borderRadius: '8px', textTransform: 'none', boxShadow: '0 4px 20px rgba(99,102,241,0.3)', '&:hover': { bgcolor: '#5254CC', transform: 'translateY(-2px)' }, transition: 'all 0.25s ease' }}>
              Request a Demo
            </Button>
          </Container>
        </Box>

        {/* Features */}
        <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#F8F9FF', borderTop: '1px solid #F3F4F6' }}>
          <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>Features</Typography>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.6rem', md: '2.4rem' }, letterSpacing: '-0.02em', mb: { xs: 4, md: 6 } }}>What LineDesk Does</Typography>
            <Grid container spacing={2.5}>
              {features.map((f, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Box sx={{ display: 'flex', gap: 2.5, p: 3, borderRadius: '14px', bgcolor: '#FFFFFF', border: '1px solid #E5E7EB', height: '100%' }}>
                    <Box sx={{ flexShrink: 0, width: 48, height: 48, borderRadius: '10px', bgcolor: '#EEF2FF', border: '1px solid #C7D2FE', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366F1' }}>
                      {f.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#0F1117', mb: 0.5 }}>{f.title}</Typography>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.88rem', lineHeight: 1.7 }}>{f.description}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Use Cases */}
        <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#FFFFFF', borderTop: '1px solid #F3F4F6' }}>
          <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>Who It's For</Typography>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.6rem', md: '2.4rem' }, letterSpacing: '-0.02em', mb: { xs: 4, md: 6 } }}>Perfect For</Typography>
            <Grid container spacing={2.5}>
              {useCases.map((u, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Box sx={{ p: 3, borderRadius: '14px', bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', height: '100%' }}>
                    <Typography sx={{ fontWeight: 800, color: '#6366F1', mb: 1.5 }}>{u.title}</Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.88rem', lineHeight: 1.75 }}>{u.body}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Deployment Options */}
        <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#FFFFFF', borderTop: '1px solid #F3F4F6' }}>
          <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>Deployment</Typography>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.6rem', md: '2.4rem' }, letterSpacing: '-0.02em', mb: 2 }}>Made to fit your business.</Typography>
            <Typography sx={{ color: '#6B7280', fontSize: '0.95rem', lineHeight: 1.8, mb: { xs: 4, md: 6 }, maxWidth: 560 }}>
              LineDesk isn't one-size-fits-all. Choose how you want it deployed, branded, and hosted.
            </Typography>
            <Grid container spacing={2.5}>
              {[
                {
                  icon: '🎨',
                  title: 'Custom Branding',
                  body: 'Your logo, your colours, your name throughout the app. Your team and customers see your brand — not ours.',
                  tags: ['White-label', 'Logo & colours', 'Custom email templates'],
                },
                {
                  icon: '🌐',
                  title: 'Custom Domain',
                  body: 'Run LineDesk on your own domain — e.g. records.yourcompany.com. Full SSL included. Looks like a product you built in-house.',
                  tags: ['records.yourcompany.com', 'SSL included', 'DNS setup support'],
                },
                {
                  icon: '🖥️',
                  title: 'Self-Hosting',
                  body: 'Deploy LineDesk on your own servers or cloud account. Your production data stays entirely within your infrastructure.',
                  tags: ['Your servers', 'Your cloud account', 'Full data ownership'],
                },
              ].map((opt) => (
                <Grid item xs={12} md={4} key={opt.title}>
                  <Box sx={{ p: 3.5, borderRadius: '16px', bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontSize: '2rem', mb: 2 }}>{opt.icon}</Typography>
                    <Typography sx={{ fontWeight: 800, color: '#0F1117', fontSize: '1.05rem', mb: 1.5 }}>{opt.title}</Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.88rem', lineHeight: 1.75, mb: 2.5, flexGrow: 1 }}>{opt.body}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {opt.tags.map(t => (
                        <Box key={t} sx={{ px: 1.5, py: 0.4, borderRadius: '100px', bgcolor: '#EEF2FF', border: '1px solid #C7D2FE' }}>
                          <Typography sx={{ color: '#4F46E5', fontSize: '0.72rem', fontWeight: 600 }}>{t}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Contact */}
        <Box id="linedesk-contact" sx={{ py: { xs: 6, md: 10 }, bgcolor: '#F8F9FF', borderTop: '1px solid #F3F4F6' }}>
          <Container maxWidth="md" sx={{ px: { xs: 3, md: 4 } }}>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>Get Started</Typography>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.6rem', md: '2.4rem' }, letterSpacing: '-0.02em', mb: 1.5 }}>Request a LineDesk Demo</Typography>
            <Typography sx={{ color: '#6B7280', mb: 4, lineHeight: 1.8 }}>Tell us about your current tracking process and we'll show you exactly how LineDesk replaces it.</Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ bgcolor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '20px', p: { xs: 3, md: 4 } }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Name" required value={formData.name} onChange={set('name')} sx={fieldSx} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Email" type="email" required value={formData.email} onChange={set('email')} sx={fieldSx} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Company" value={formData.company} onChange={set('company')} sx={fieldSx} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Phone" value={formData.phone} onChange={set('phone')} sx={fieldSx} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Describe your current tracking process" multiline rows={4} required value={formData.message} onChange={set('message')} sx={fieldSx} /></Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" size="large" disabled={sending} endIcon={<ArrowForwardIcon />} fullWidth
                    sx={{ bgcolor: '#6366F1', py: 1.6, fontWeight: 700, fontSize: '1rem', borderRadius: '10px', textTransform: 'none', boxShadow: '0 4px 20px rgba(99,102,241,0.3)', '&:hover': { bgcolor: '#5254CC' }, transition: 'all 0.25s ease' }}>
                    {sending ? 'Sending...' : 'Request Demo'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>
      </Box>
    </Layout>
  );
};

export default LineDeskPage;
